import { ContactUs, CopyrightFooter, DataGrid } from "app/components";
import { Container, IAppState } from "app/containers/Container";
import * as React from "react";
import { Col, Row } from "react-bootstrap";
import { GIVAnnualizedDataSource } from "./DataSources";
import { ResourceAction } from "app/redux/Resources/actions";
import { GivIndex } from "app/components/visualizations/GivIndex";
import { GivIndexTable } from "app/components/visualizations/GivIndexTable";
import { DataAsOf } from "app/components/visualizations/DataAsOf";
import { DataAsOfLeft } from "app/components/visualizations/DataAsOfLeft";

interface IGIVState {
  trendTitle: string;
  trendSubtitle: string;
  givSeries: any[];
  sixtyFortySeries: any[];
  subtitleX: number;
  subtitleY: number;
  isLoggedIn: boolean;
  apiKey: string;
  username: string;
}

class GIV extends Container<{}, IGIVState> {
  state: IGIVState = {
    trendSubtitle: "",
    trendTitle: "",
    givSeries: [],
    sixtyFortySeries: [],
    subtitleX: -140,
    subtitleY: 475,
    isLoggedIn: false,
    apiKey: null,
    username: null,
  };

  mapAppStateToContainerState(appState: IAppState) {
    const isLoggedIn = !!appState.User.username;
    const username = appState.User.username;
    const key = appState.User.token;
    return {
      trendSubtitle: this.state.trendSubtitle,
      trendTitle: this.state.trendTitle,
      givSeries: this.state.givSeries,
      sixtyFortySeries: this.state.sixtyFortySeries,
      subtitleX: this.state.subtitleX,
      subtitleY: this.state.subtitleY,
      isLoggedIn,
      username,
      apiKey: key,
    };
  }

  getChartConfig() {
    const config = {
      chart: { type: "column", height: 258 },
      yAxis: {
        labels: {
          format: "{value}%",
        },
      },
      legend: { layout: "vertical", align: "left" },
      xAxis: { categories: ["1 Year", "3 Year", "5 Year"] },
      tooltip: {
        pointFormat: "{point.series.name}: {point.y}%",
        headerFormat: null,
        footerFormat: null,
      },
      credits: {
        enabled: false,
      },
    };
    return config;
  }

  public getGiveIndexConfig(trendTitle, trendSubtitle, giv, sixtyForty) {
    const title = "Performance Data to " + trendTitle;
    const subtitle = "Base value of 100 beginning " + trendSubtitle;

    return {
      chart: {
        type: "line",
        height: 80 + "%",
      },
      title: {
        text: title,
        x: -80,
        y: 100,
      },
      subtitle: {
        text: subtitle,
        x: this.state.subtitleX,
        y: this.state.subtitleY,
      },
      xAxis: {
        type: "datetime",
      },
      yAxis: {
        min: 50,
        title: null,
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        area: {
          marker: {
            enabled: false,
          },
        },
      },
      tooltip: {
        headerFormat: "<b>{series.name}</b><br>",
        pointFormat: "",
      },
      series: [
        {
          name: "GIV Index",
          colorByPoint: false,
          color: "#022636",
          fillOpacity: 0.1,
          lineColor: "#022636",
          lineWidth: 4,
          data: giv,
        },
        {
          name: "60/40 Index",
          colorByPoint: false,
          color: "#a1a1a1",
          dashStyle: "ShortDash",
          fillOpacity: 0.1,
          lineColor: "#a1a1a1",
          data: sixtyForty,
          label: {
            enabled: true,
          },
        },
      ],
    };
  }

  public round(numberToRound: number, precision: number) {
    return (Math.round(numberToRound * 10) / 10).toFixed(precision);
  }

  downloadIndex() {
    const fileName = "GIV Index.pdf";
    if (this.state.isLoggedIn) {
      //NOTE: download file
      ResourceAction.getFileUrl(
        this.state.username,
        this.state.apiKey,
        fileName
      )
        .then((response) => {
          window.open(response.resource.url);
        })
        .catch((error) => {
          // tslint:disable-next-line:no-console
          console.log(error);
        });
    } else {
      //NOTE: Redirect to login page
      location.hash = "#/login?fileName=" + fileName + "&page=indices";
    }
  }

  render() {
    new DataAsOf().getDataAsOfDate();
    return (
      <section className="padding-top animated fadeIn">
        <section className="container full-width-tabs">
          <header className="container">
            <h3 className="hidden-xs hidden-sm text-center">
              GIV Index: Foundation Investment Performance
            </h3>
            <h5 className="hidden-md hidden-lg text-center">
              GIV Index: Foundation Investment Performance
            </h5>
            The FoundationMark Grantmaker Investment Valuation Index (“GIV”)
            tracks the investment performance of private foundations. Using data
            drawn from over 40,000 foundations, the GIV Index is the most
            comprehensive measure of foundation performance available.
          </header>
          <Row>
            <Col sm={12}>
              <DataAsOf />
            </Col>
          </Row>
          <Row>
            <Col sm={12} md={8}>
              <GivIndex chartHeight={80} titleFontSize="1.8rem"></GivIndex>
            </Col>
            <Col sm={12} md={4}>
              <GivIndexTable />
            </Col>
          </Row>

          <Row>
            <Col sm={12}>
              <header style={{ padding: "1.0rem" }}>
                <DataAsOfLeft />
                <span style={{ float: "right" }}>
                  <a onClick={() => this.downloadIndex()}>Download GIV Index</a>
                </span>
              </header><br />
              <DataGrid
                DataSource={GIVAnnualizedDataSource}
                pageSizes={[10, 15]}
                hideInputs={true}
                tableHeaderClassName="text-right"
                columns={[
                  {
                    label: "",
                    pathExpression: "$.index",
                    customRender: (data) => {
                      const weight =
                        data.index.trim() === "GIV Index" ? "bold" : "unset";
                      return (
                        <span style={{ fontWeight: weight }}>{data.index}</span>
                      );
                    },
                  },
                  {
                    label: "1 Mo",
                    pathExpression: "$.oneMonth",
                    customRender: (data) => {
                      const weight =
                        data.index.trim() === "GIV Index" ? "bold" : "unset";
                      return (
                        <span style={{ float: "right", fontWeight: weight }}>
                          {this.round(data.oneMonth, 1) + "%"}
                        </span>
                      );
                    },
                  },
                  {
                    label: "3 Mo",
                    pathExpression: "$.threeMonth",
                    customRender: (data) => {
                      const weight =
                        data.index.trim() === "GIV Index" ? "bold" : "unset";
                      return (
                        <span style={{ float: "right", fontWeight: weight }}>
                          {this.round(data.threeMonth, 1) + "%"}
                        </span>
                      );
                    },
                  },
                  {
                    label: "YTD",
                    pathExpression: "$.yearToDate",
                    customRender: (data) => {
                      return (
                        <span style={{ float: "right", fontWeight: "bold" }}>
                          {this.round(data.yearToDate, 1) + "%"}
                        </span>
                      );
                    },
                  },
                  {
                    label: "1 Yr",
                    pathExpression: "$.year",
                    customRender: (data) => {
                      const weight =
                        data.index.trim() === "GIV Index" ? "bold" : "unset";
                      return (
                        <span style={{ float: "right", fontWeight: weight }}>
                          {this.round(data.year, 1) + "%"}
                        </span>
                      );
                    },
                  },
                  {
                    label: "3 Yr",
                    pathExpression: "$.threeYear",
                    customRender: (data) => {
                      const weight =
                        data.index.trim() === "GIV Index" ? "bold" : "unset";
                      return (
                        <span style={{ float: "right", fontWeight: weight }}>
                          {this.round(data.threeYear, 1) + "%"}
                        </span>
                      );
                    },
                  },
                  {
                    label: "5 Yr",
                    pathExpression: "$.fiveYear",
                    customRender: (data) => {
                      const weight =
                        data.index.trim() === "GIV Index" ? "bold" : "unset";
                      return (
                        <span style={{ float: "right", fontWeight: weight }}>
                          {this.round(data.fiveYear, 1) + "%"}
                        </span>
                      );
                    },
                  },
                  {
                    label: "10 Yr",
                    pathExpression: "$.tenYear",
                    customRender: (data) => {
                      const weight =
                        data.index.trim() === "GIV Index" ? "bold" : "unset";
                      return (
                        <span style={{ float: "right", fontWeight: weight }}>
                          {this.round(data.tenYear, 1) + "%"}
                        </span>
                      );
                    },
                  },
                ]}
              ></DataGrid>
              <section style={{ fontSize: "1.2rem" }}>
                &#42;The 60/40 Balanced Portfolio represents the traditional
                institutional allocation to equities and fixed income with
                weightings of 60&#37; in the S&amp;P 500 and 40&#37; in the
                Bloomberg Barclays Aggregate Index, rebalanced monthly.
                <br />3 Year, 5 Year and 10 Year performance is annualized
              </section>

            </Col>
          </Row>
        </section>

        <footer className="margin-y-xl">
          <ContactUs />
          <CopyrightFooter />
        </footer>
      </section>
    );
  }
}

export { GIV };



// WEBPACK FOOTER //
// ./src/app/containers/GIV/index.tsx